/** Trial-balance check for the single conversion journal.
 * The journal is dated the day before the books start. Receivable lines are
 * assets subtyped `accounts_receivable`. Payable lines are liabilities
 * subtyped `accounts_payable`. Every other account is an ordinary line.
 */

export type ConversionAccountKind = "receivable" | "payable" | "other";

export interface ConversionDraftLine {
  account_id: string;
  debit_cents: number;
  credit_cents: number;
  kind: ConversionAccountKind;
}

export interface ConversionReview {
  entry_date: string;
  debit_cents: number;
  credit_cents: number;
  receivables_cents: number;
  payables_cents: number;
  open_invoices_cents: number;
  open_bills_cents: number;
  balanced: boolean;
  receivables_match: boolean;
  payables_match: boolean;
  save_allowed: boolean;
  line_count: number;
}

type ConversionReviewError =
  | "no_previous_day"
  | "invalid_line"
  | "overflow";

export function conversionAccountKind(
  accountType: string | undefined,
  subType: string | undefined,
): ConversionAccountKind {
  if (accountType === "asset" && subType === "accounts_receivable") {
    return "receivable";
  }
  if (accountType === "liability" && subType === "accounts_payable") {
    return "payable";
  }
  return "other";
}

/** The journal date: the calendar day before `booksStart` (`YYYY-MM-DD`). */
export function previousIsoDay(booksStart: string): string | null {
  return shiftIsoDay(booksStart, -1);
}

export function nextIsoDay(isoDay: string): string | null {
  return shiftIsoDay(isoDay, 1);
}

function shiftIsoDay(isoDay: string, days: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return null;
  const year = Number(isoDay.slice(0, 4));
  const month = Number(isoDay.slice(5, 7));
  const day = Number(isoDay.slice(8, 10));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  date.setUTCDate(date.getUTCDate() + days);
  const nextYear = date.getUTCFullYear();
  if (nextYear < 1 || nextYear > 9999) return null;
  const monthText = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dayText = String(date.getUTCDate()).padStart(2, "0");
  return `${String(nextYear).padStart(4, "0")}-${monthText}-${dayText}`;
}

function addCents(total: number, amount: number): number | null {
  const next = total + amount;
  return Number.isSafeInteger(next) ? next : null;
}

/** Reviews lines the caller has already checked for a single positive side. */
export function reviewConversionLines(
  booksStart: string,
  lines: readonly ConversionDraftLine[],
  open: { invoices_cents: number; bills_cents: number },
):
  | { ok: true; review: ConversionReview }
  | { ok: false; error: ConversionReviewError } {
  const entryDate = previousIsoDay(booksStart);
  if (!entryDate) return { ok: false, error: "no_previous_day" };
  let debitCents = 0;
  let creditCents = 0;
  let receivablesCents = 0;
  let payablesCents = 0;
  for (const line of lines) {
    const debitOnly = line.debit_cents > 0 && line.credit_cents === 0;
    const creditOnly = line.credit_cents > 0 && line.debit_cents === 0;
    if (
      !Number.isInteger(line.debit_cents) ||
      !Number.isInteger(line.credit_cents) ||
      (!debitOnly && !creditOnly)
    ) {
      return { ok: false, error: "invalid_line" };
    }
    const nextDebit = addCents(debitCents, line.debit_cents);
    const nextCredit = addCents(creditCents, line.credit_cents);
    if (nextDebit === null || nextCredit === null) {
      return { ok: false, error: "overflow" };
    }
    debitCents = nextDebit;
    creditCents = nextCredit;
    if (line.kind === "receivable") {
      const next = addCents(receivablesCents, line.debit_cents - line.credit_cents);
      if (next === null) return { ok: false, error: "overflow" };
      receivablesCents = next;
    } else if (line.kind === "payable") {
      const next = addCents(payablesCents, line.credit_cents - line.debit_cents);
      if (next === null) return { ok: false, error: "overflow" };
      payablesCents = next;
    }
  }
  const balanced = lines.length >= 2 && debitCents === creditCents;
  const receivablesMatch = receivablesCents === open.invoices_cents;
  const payablesMatch = payablesCents === open.bills_cents;
  return {
    ok: true,
    review: {
      entry_date: entryDate,
      debit_cents: debitCents,
      credit_cents: creditCents,
      receivables_cents: receivablesCents,
      payables_cents: payablesCents,
      open_invoices_cents: open.invoices_cents,
      open_bills_cents: open.bills_cents,
      balanced,
      receivables_match: receivablesMatch,
      payables_match: payablesMatch,
      save_allowed: balanced && receivablesMatch && payablesMatch,
      line_count: lines.length,
    },
  };
}
